import { GlobalStyles } from '@mui/material';
import {
  StyledEngineProvider,
  ThemeProvider,
  useColorScheme,
} from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useContext, useEffect } from 'react';
import { AuthContext } from 'auth/AuthProvider';
import { setColorScheme } from './colorSchemeStore';
import theme from './theme';
import useDarkModeEnabled from './useDarkModeEnabled';

// MUI's default key for the saved light/dark/system choice.
const MODE_STORAGE_KEY = 'mui-mode';

const getStoredMode = () => {
  try {
    const mode = localStorage.getItem(MODE_STORAGE_KEY);
    if (mode === 'light' || mode === 'dark' || mode === 'system') {
      return mode;
    }
  } catch {
    // Fall back to the default when local storage is unavailable.
  }
  return 'system';
};

// No `CssBaseline` yet: it would also reset typography and box-sizing for
// the whole app. Only style the page itself in dark mode.
const globalStyles = (
  <GlobalStyles
    styles={{
      '[data-mui-color-scheme="dark"]': {
        colorScheme: 'dark',
        body: {
          backgroundColor: 'var(--mui-palette-background-default)',
          color: 'var(--mui-palette-text-primary)',
        },
      },
    }}
  />
);

// MUI reads the saved mode only once, so re-apply it when the beta flag
// changes. With the flag off the provider has no storage, so forcing light
// here does not overwrite the user's saved choice.
const ColorSchemeGate = ({ enabled }: { enabled: boolean }) => {
  const { setMode } = useColorScheme();

  useEffect(() => {
    setMode(enabled ? getStoredMode() : 'light');
  }, [enabled, setMode]);

  return null;
};

// Mirrors the resolved scheme for code outside React (GoJS, Chart.js).
const ColorSchemeSync = () => {
  const { colorScheme } = useColorScheme();

  useEffect(() => {
    setColorScheme(colorScheme === 'dark' ? 'dark' : 'light');
  }, [colorScheme]);

  return null;
};

const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  // The beta flag depends on the user's role, which is known once auth has
  // initialised; consuming the context re-renders this when that happens.
  useContext(AuthContext);
  const darkModeEnabled = useDarkModeEnabled();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider
        theme={theme}
        defaultMode={darkModeEnabled ? 'system' : 'light'}
        storageManager={darkModeEnabled ? undefined : null}
        disableTransitionOnChange
      >
        <ColorSchemeGate enabled={darkModeEnabled} />
        <ColorSchemeSync />
        {globalStyles}
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default AppThemeProvider;
